import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FiEye, FiEyeOff, FiSave, FiSend, FiTrash2, FiX } from 'react-icons/fi';
import { css } from 'twin.macro';
import { toast } from 'react-toastify';
import { emailBroadcastService } from '@/_services/api/emailIntegration/emailBroadcast';
import { fetchWrapper } from '@/_helpers';
import { VscLoading } from 'react-icons/vsc';

const fieldStyle = css`
    display: block;
    width: 100%;
    min-height: 38px;
    border: 1px solid #d8dde6;
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 14px;
`;

const rowStyle = css`
    display: grid;
    grid-template-columns: 84px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
    margin-bottom: 12px;
`;

const labelStyle = css`
    margin-top: 9px;
    font-size: 13px;
    font-weight: 600;
    color: #44505f;
    text-align: right;
    white-space: nowrap;
`;

const recipientsWrapStyle = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const recipientBadgeStyle = css`
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 0.6rem 0.8rem;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    background: #eef2ff;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: default;
`;

const bodyWrap = css`
    min-height: 70vh;
`;

const htmlPreviewStyle = css`
    border: 1px solid #d8dde6;
    border-radius: 10px;
    padding: 16px;
    min-height: 150px;
    background: #fafbfc;
    max-height: 400px;
    overflow-y: auto;

    /* Safe styling for preview content */
    & > * {
        max-width: 100%;
    }

    img {
        max-width: 100%;
        height: auto;
    }

    table {
        width: 100%;
        border-collapse: collapse;
    }

    table td,
    table th {
        border: 1px solid #d8dde6;
        padding: 6px 8px;
    }

    a {
        color: #0066cc;
        text-decoration: underline;
    }
`;

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

const parseEmails = (value = '') => [
    ...new Set((String(value || '').match(EMAIL_REGEX) || []).map((item) => item.trim().toLowerCase())),
];

const EmailBroadcastComposer = ({
    show,
    onClose,
    teamId,
    draftId,
    existingDraft,
    onDraftSaved,
    onBroadcastSent,
    readOnly = false,
    disabled = false,
}) => {
    const isReadOnly = readOnly || disabled;
    const [subject, setSubject] = useState('');
    const [bodyContent, setBodyContent] = useState('');
    const [isHtmlMode, setIsHtmlMode] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [recipientInput, setRecipientInput] = useState('');
    const [fromOptions, setFromOptions] = useState([]);
    const [selectedFromAccountId, setSelectedFromAccountId] = useState('');
    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [quota, setQuota] = useState(null);
    const [loadingQuota, setLoadingQuota] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadedDraft, setLoadedDraft] = useState(null);

    const recipients = parseEmails(recipientInput);

    const populateComposer = (draft) => {
        if (!draft) return;
        const content = draft?.emailBroadcastDraftSnapshot || draft || {};
        const nextBodyText = content.bodyText || '';
        const nextBodyHtml = content.bodyHtml || '';
        const nextBody = nextBodyText || nextBodyHtml || '';

        setSubject(content.subject || draft.subject || '');
        setBodyContent(nextBody);
        setIsHtmlMode(!!nextBodyHtml);
        setRecipientInput((content.recipients || draft.recipients || []).map((r) => r.email).join(', '));

        if (content.externalEmailAccount || draft.externalEmailAccount) {
            setSelectedFromAccountId(String(content.externalEmailAccount || draft.externalEmailAccount));
        }
    };

    const fetchDraftById = async (id) => {
        if (!id) return;

        setLoading(true);
        try {
            const draft = await emailBroadcastService.getDraftById(id);
            setLoadedDraft(draft);
            populateComposer(draft);

            return draft;
        } catch (err) {
            toast.error(`Failed to load draft: ${err.message || err}`);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return;

        if (isReadOnly && existingDraft) {
            populateComposer(existingDraft);
            return;
        }

        if (draftId) {
            fetchDraftById(draftId);
            return;
        }

        if (existingDraft?._id) {
            fetchDraftById(existingDraft._id);
            return;
        }

        if (existingDraft) {
            populateComposer(existingDraft);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, draftId, existingDraft, isReadOnly]);

    useEffect(() => {
        const fetchContactsAndFromOptions = async () => {
            if (!show) return;

            setLoadingContacts(true);

            try {
                const [contactsResult, fromOptionsResult] = await Promise.allSettled([
                    teamId ? fetchWrapper.get(`/contacts?team=${teamId}&limit=500`) : Promise.resolve([]),
                    fetchWrapper.get('email-integrations/accounts'),
                ]);

                if (contactsResult.status === 'fulfilled') {
                    const contactsData = contactsResult.value;
                    const filteredContacts = (contactsData || []).filter((c) => c.email);
                    setContacts(filteredContacts);
                } else {
                    console.error('Failed to fetch contacts:', contactsResult.reason);
                    setContacts([]);
                }

                if (fromOptionsResult.status === 'fulfilled') {
                    const fromOptionsData = fromOptionsResult.value;
                    const accounts = fromOptionsData.results || [];
                    setFromOptions(accounts);

                    if (accounts.length > 0 && !selectedFromAccountId) {
                        setSelectedFromAccountId(String(accounts[0]._id));
                    }
                } else {
                    console.error('Failed to fetch from options:', fromOptionsResult.reason);
                    setFromOptions([]);
                }
            } catch (error) {
                console.error('Error fetching contacts and from options:', error);
                toast.error('Failed to load contacts. Please refresh the page.');
            } finally {
                setLoadingContacts(false);
            }
        };

        fetchContactsAndFromOptions();
    }, [show, teamId]);

    useEffect(() => {
        const fetchQuotaForAccount = async () => {
            if (!selectedFromAccountId || !show) {
                setQuota(null);
                setLoadingQuota(false);
                return;
            }
            setLoadingQuota(true);
            try {
                const res = await emailBroadcastService.getQuotaStatus(selectedFromAccountId);
                setQuota(res);
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return;
                }
                console.error('Failed to fetch quota:', error);
                setQuota(null);
            } finally {
                setLoadingQuota(false);
            }
        };

        fetchQuotaForAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFromAccountId, show]);

    const sentToday = quota?.sent ?? 0;
    const quotaPerDay = quota?.quotaPerDay ?? 0;

    const addRecipientFromContact = (contactId) => {
        const contact = contacts.find((c) => c._id === contactId);
        if (contact) {
            const current = parseEmails(recipientInput);
            if (!current.includes(contact.email.toLowerCase())) {
                const newInput = recipientInput ? `${recipientInput}, ${contact.email}` : contact.email;
                setRecipientInput(newInput);
            }
        }
    };

    const selectAllContacts = () => {
        const allEmails = contacts.map((c) => c.email).filter(Boolean);
        const current = parseEmails(recipientInput);
        const newEmails = [...current];
        allEmails.forEach((email) => {
            if (!newEmails.includes(email.toLowerCase())) {
                newEmails.push(email.toLowerCase());
            }
        });
        setRecipientInput(newEmails.join(', '));
    };

    const handleSaveDraft = async () => {
        if (!subject.trim()) {
            toast.error('Subject is required');
            return;
        }
        if (!selectedFromAccountId) {
            toast.error('Please select a From Email account');
            return;
        }
        if (recipients.length === 0) {
            toast.error('Please add at least one recipient');
            return;
        }
        const contactMap = new Map();
        contacts.forEach((contact) => {
            if (contact.email) {
                contactMap.set(contact.email.toLowerCase(), contact);
            }
        });

        const payload = {
            team: teamId,
            subject: subject.trim(),
            bodyText: isHtmlMode ? '' : bodyContent.trim(),
            bodyHtml: isHtmlMode ? bodyContent.trim() : '',
            externalEmailAccount: selectedFromAccountId,
            recipients: recipients.map((email) => {
                const contact = contactMap.get(email.toLowerCase());
                return {
                    email: email,
                    name: contact?.name || '',
                    contact: contact?._id || null,
                };
            }),
        };
        let emailbroadcastDraftId = '';
        setSaving(true);
        try {
            if (draftId || existingDraft?._id || loadedDraft?._id) {
                emailbroadcastDraftId = draftId || existingDraft?._id || loadedDraft?._id;
                const updatePayload = {
                    subject: payload.subject,
                    bodyText: payload.bodyText,
                    bodyHtml: payload.bodyHtml,
                    recipients: payload.recipients,
                    externalEmailAccount: selectedFromAccountId,
                };
                await emailBroadcastService.updateDraft(emailbroadcastDraftId, updatePayload);
                toast.success('Draft updated');
                if (onDraftSaved) onDraftSaved(emailbroadcastDraftId);
            } else {
                const res = await emailBroadcastService.createDraft(payload);
                emailbroadcastDraftId = res?._id;
                toast.success('Draft saved');
                if (onDraftSaved) onDraftSaved(res._id);
            }
            return emailbroadcastDraftId;
        } catch (err) {
            toast.error(`Failed to save draft: ${err.message || err}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSend = async () => {
        if (!selectedFromAccountId) {
            toast.error('Please select a From Email account');
            return;
        }

        setSaving(true);
        try {
            const savedDraftId = await handleSaveDraft();
            setSending(true);
            if (savedDraftId) {
                await emailBroadcastService.createBroadcastFromDraft(savedDraftId);
                toast.info(`Broadcast started`);
                onClose?.(true);
            }
        } catch (err) {
            toast.error(`Failed to save draft: ${err.message || err}`);
        } finally {
            setSaving(false);
            setSending(false);
        }
    };

    const clearComposer = () => {
        setSubject('');
        setBodyContent('');
        setRecipientInput('');
        setIsHtmlMode(false);
        setShowPreview(false);
        setLoadedDraft(null);
    };

    const handleModeChange = (htmlMode) => {
        setIsHtmlMode(htmlMode);
        if (showPreview) {
            setShowPreview(false);
        }
    };

    return (
        <Modal show={show} onHide={() => onClose?.(false)} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <span>Email Broadcast</span>
                    {isReadOnly ? (
                        <span className="badge bg-info ms-2">View Only</span>
                    ) : draftId || existingDraft?._id || loadedDraft?._id ? (
                        <span className="badge bg-secondary ms-2">Draft</span>
                    ) : null}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body css={bodyWrap}>
                {loading ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Form>
                        {/* From Account */}
                        <div css={rowStyle}>
                            <div css={labelStyle}>From</div>
                            <div>
                                <Form.Control
                                    as="select"
                                    css={fieldStyle}
                                    value={selectedFromAccountId}
                                    onChange={(e) => setSelectedFromAccountId(e.target.value)}
                                    disabled={isReadOnly || saving || sending}
                                >
                                    <option value="">Select Email account...</option>
                                    {fromOptions.map((opt) => (
                                        <option key={opt._id} value={String(opt._id)}>
                                            {opt.email} {opt.name ? `(${opt.name})` : ''}
                                        </option>
                                    ))}
                                </Form.Control>
                                {fromOptions.length === 0 && (
                                    <div className="text-muted small mt-1">
                                        No Email accounts connected. Go to Settings &gt; Email Integrations to connect
                                        one.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Subject */}
                        <div css={rowStyle}>
                            <div css={labelStyle}>Subject</div>
                            <Form.Control
                                type="text"
                                css={fieldStyle}
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Broadcast subject..."
                                disabled={isReadOnly || saving || sending}
                            />
                        </div>

                        {/* Recipients */}
                        {!isReadOnly && (
                            <div css={rowStyle}>
                                <div css={labelStyle}>To</div>
                                <div>
                                    <Form.Control
                                        type="text"
                                        css={css`
                                            ${fieldStyle}
                                            flex: 1;
                                            min-width: 0;
                                        `}
                                        value={recipientInput}
                                        onChange={(e) => setRecipientInput(e.target.value)}
                                        placeholder="Enter emails separated by commas"
                                        disabled={saving || sending}
                                    />
                                    <div className="d-flex gap-2 mb-2 align-items-center">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={selectAllContacts}
                                            disabled={loadingContacts || !contacts.length || saving || sending}
                                            className="text-nowrap"
                                            css={css`
                                                white-space: nowrap;
                                                flex-shrink: 0;
                                            `}
                                        >
                                            All Contacts
                                        </Button>
                                        <select
                                            css={css`
                                                width: auto;
                                                min-width: 180px;
                                                flex-shrink: 0;
                                                min-height: 38px;
                                                border: 1px solid #d8dde6;
                                                padding: 8px 12px;
                                                font-size: 14px;
                                                background: #fff;
                                            `}
                                            value=""
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    addRecipientFromContact(e.target.value);
                                                    e.target.value = '';
                                                }
                                            }}
                                            disabled={saving || sending || loadingContacts}
                                        >
                                            <option value="">Pick from contacts...</option>
                                            {contacts.map((c) => (
                                                <option key={c._id} value={c._id}>
                                                    {c.name || '-'} - {c.email}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Recipient pills */}
                                    {recipients.length > 0 && (
                                        <div css={recipientsWrapStyle}>
                                            {recipients.map((email) => (
                                                <span key={email} css={recipientBadgeStyle}>
                                                    {email}
                                                    <FiX
                                                        css={css`
                                                            margin-left: 0.5rem;
                                                            cursor: pointer;
                                                            &:hover {
                                                                color: red;
                                                            }
                                                        `}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const current = parseEmails(recipientInput);
                                                            const filtered = current.filter((e) => e !== email);
                                                            setRecipientInput(filtered.join(', '));
                                                        }}
                                                    />
                                                </span>
                                            ))}
                                            {recipients.length > 1 && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => setRecipientInput('')}
                                                    disabled={saving || sending}
                                                >
                                                    <FiTrash2 /> Clear all
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Body with HTML toggle */}
                        <div css={rowStyle}>
                            <div css={labelStyle}>Body</div>
                            <div>
                                <div className="d-flex gap-2 mb-2 flex-wrap">
                                    <Button
                                        size="sm"
                                        variant={!isHtmlMode ? 'primary' : 'outline-secondary'}
                                        onClick={() => handleModeChange(false)}
                                        disabled={saving || sending}
                                    >
                                        Plain Text
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={isHtmlMode ? 'primary' : 'outline-secondary'}
                                        onClick={() => handleModeChange(true)}
                                        disabled={saving || sending}
                                    >
                                        HTML
                                    </Button>
                                    {/* Preview button - only shown in HTML mode */}
                                    {isHtmlMode && bodyContent.trim() && (
                                        <Button
                                            size="sm"
                                            variant="outline-info"
                                            onClick={() => setShowPreview(!showPreview)}
                                            disabled={saving || sending}
                                        >
                                            {showPreview ? <FiEyeOff className="me-1" /> : <FiEye className="me-1" />}
                                            {showPreview ? 'Hide Preview' : 'Preview'}
                                        </Button>
                                    )}
                                </div>

                                {/* Preview Section - Separate Div */}
                                {isHtmlMode && showPreview && (
                                    <div css={htmlPreviewStyle}>
                                        <div dangerouslySetInnerHTML={{ __html: bodyContent }} />
                                    </div>
                                )}

                                {/* <iframe
                                            srcDoc={`
             ${bodyContent}
            `}
                                            style={{
                                                width: '100%',
                                                height: '500px',
                                                border: 'none',
                                                borderRadius: '8px',
                                                background: '#ffffff',
                                            }}
                                            title="Email Preview"
                                            sandbox="allow-same-origin"
                                        /> */}

                                {/* Text Editor - Hidden when preview is shown in HTML mode */}
                                {(!isHtmlMode || !showPreview) && (
                                    <Form.Control
                                        as="textarea"
                                        rows={10}
                                        css={css`
                                            ${fieldStyle}
                                            min-height: 180px;
                                            resize: vertical;
                                            font-family: ${isHtmlMode ? 'monospace' : 'inherit'};
                                        `}
                                        value={bodyContent}
                                        onChange={(e) => setBodyContent(e.target.value)}
                                        placeholder={
                                            isHtmlMode ? 'Enter HTML email body...' : 'Enter your email body text...'
                                        }
                                        disabled={isReadOnly || saving || sending}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Quota/Usage preview */}
                        {!isReadOnly && (
                            <div css={rowStyle}>
                                <div css={labelStyle}>Usage</div>
                                <div className="mt-2">
                                    {loadingQuota ? (
                                        <span className="text-muted small">Loading usage stats...</span>
                                    ) : quota ? (
                                        <div className="text-muted small">
                                            <p>
                                                Emails sent today: <strong>{sentToday}</strong>
                                            </p>
                                            <p>
                                                Standard Daily Limit:
                                                <strong>{quotaPerDay}</strong>
                                            </p>
                                        </div>
                                    ) : (
                                        <span className="text-warning small">
                                            {selectedFromAccountId
                                                ? 'Could not load usage info'
                                                : 'Select an Email account to see daily usage'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </Form>
                )}
            </Modal.Body>

            {!isReadOnly && (
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => onClose?.(false)} disabled={saving || sending}>
                        Cancel
                    </Button>
                    <Button variant="outline-secondary" onClick={clearComposer} disabled={saving || sending}>
                        Clear
                    </Button>
                    <Button
                        variant="outline-primary"
                        onClick={handleSaveDraft}
                        disabled={
                            saving || sending || !subject.trim() || recipients.length === 0 || !selectedFromAccountId
                        }
                    >
                        {saving ? <VscLoading className="spin" /> : <FiSave className="mr-1" />}
                        Save Draft
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSend}
                        disabled={
                            sending || saving || !subject.trim() || recipients.length === 0 || !selectedFromAccountId
                        }
                    >
                        {sending ? <VscLoading className="spin" /> : <FiSend className="mr-1" />}
                        Send
                    </Button>
                </Modal.Footer>
            )}
            {isReadOnly && (
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => onClose?.(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default EmailBroadcastComposer;
